// Institution-level academic config trio (PRD-38 §FR-8 / §FR-10 / §FR-12):
// period type, periods per cycle, grade scale.
//
// Lives in `components/` rather than a feature folder because three features
// render the exact same trio — the onboarding wizard's "Ciclo académico" step
// and the institution create + edit forms (architecture.md §Frontend layering:
// shared across features ⇒ promote out of `features/`). The SCHOOL override
// section is deliberately NOT this component: its fields are nullable and its
// whole job is expressing "inherit", which is a different control.
//
// Fully controlled — the parent owns the values. That is what keeps the
// prefill honest: the type→count prefill fires ONLY from the period-type
// change handler, never from an effect, so an admin who types their own count
// never has it clobbered by an unrelated re-render.
//
// PRD-60 chunk D: the count is a select of the valid range (it was a number
// input with the range spelled out in the hint), and the hint explains the
// one thing admins get wrong in sentence case instead of capitals.

import { Field, SelectInput } from "@components/ui/Field"
import type { AcademicConfigValues } from "@lib/academicConfig"
import { useTranslation } from "react-i18next"

import {
  DEFAULT_PERIODS_PER_TYPE,
  GRADE_SCALES,
  MAX_PERIODS_PER_CYCLE,
  MIN_PERIODS_PER_CYCLE,
  PERIOD_TYPES,
  type GradeScale,
  type PeriodType,
} from "@/constants/academic"

/**
 * Translate a `periods_per_cycle` error CODE from `useAcademicConfig` /
 * `useAcademicOverride` into a user-facing message. Shared so the bounds are
 * interpolated identically everywhere the control appears.
 */
const PERIOD_COUNTS = Array.from(
  { length: MAX_PERIODS_PER_CYCLE - MIN_PERIODS_PER_CYCLE + 1 },
  (_, i) => MIN_PERIODS_PER_CYCLE + i,
)

export function useAcademicPeriodsError(code: string | undefined): string | undefined {
  const { t } = useTranslation("common")
  if (!code) return undefined
  return t(`academic.error.${code}`, {
    min: MIN_PERIODS_PER_CYCLE,
    max: MAX_PERIODS_PER_CYCLE,
  })
}

interface Props {
  values: AcademicConfigValues
  onChange: (patch: Partial<AcademicConfigValues>) => void
  /** Already-translated validation message for the count field. */
  periodsError?: string
  disabled?: boolean
}

export function AcademicConfigFields({ values, onChange, periodsError, disabled }: Props) {
  const { t } = useTranslation("common")

  // PRD-38 §FR-8 — picking a type re-prefills the count with that type's
  // PROGRAM-WIDE default (3 / 6 / 9 — a 3-year secundaria), not a per-year
  // one. Unconditional on purpose: the prefill IS the point of the type
  // selector, and the count sits right below, still editable. Because this
  // only runs on a real change event, a hand-typed count survives every
  // subsequent render.
  const handlePeriodTypeChange = (period_type: PeriodType) =>
    onChange({ period_type, periods_per_cycle: DEFAULT_PERIODS_PER_TYPE[period_type] })

  return (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label={t("academic.period_type.label")} required>
          {({ id }) => (
            <SelectInput
              id={id}
              value={values.period_type}
              disabled={disabled}
              onChange={(e) => handlePeriodTypeChange(e.target.value as PeriodType)}
            >
              {PERIOD_TYPES.map((type) => (
                <option key={type} value={type}>
                  {t(`academic.period_type.${type}`)}
                </option>
              ))}
            </SelectInput>
          )}
        </Field>

        <Field
          label={t("academic.periods_per_cycle.label")}
          required
          hint={t("academic.periods_per_cycle.hint", {
            unit: t(`academic.period_unit.${values.period_type}`),
          })}
          error={periodsError}
        >
          {({ id, describedBy, invalid }) => (
            <SelectInput
              id={id}
              aria-describedby={describedBy}
              aria-invalid={invalid}
              disabled={disabled}
              value={Number.isNaN(values.periods_per_cycle) ? "" : String(values.periods_per_cycle)}
              onChange={(e) => onChange({ periods_per_cycle: Number(e.target.value) })}
            >
              {PERIOD_COUNTS.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </SelectInput>
          )}
        </Field>
      </div>

      <Field label={t("academic.grade_scale.label")} hint={t("academic.grade_scale.hint")} required>
        {({ id, describedBy }) => (
          <SelectInput
            id={id}
            aria-describedby={describedBy}
            value={values.grade_scale}
            disabled={disabled}
            onChange={(e) => onChange({ grade_scale: e.target.value as GradeScale })}
          >
            {GRADE_SCALES.map((scale) => (
              <option key={scale} value={scale}>
                {t(`academic.grade_scale.${scale}`)}
              </option>
            ))}
          </SelectInput>
        )}
      </Field>
    </div>
  )
}
